import "./index.css";
import "leaflet/dist/leaflet.css";

import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import App from "./App";
import * as serviceWorker from "./serviceWorker";

const sentryDevDsn = "https://b9e24f38a912a1a653298caaa47911a4@apm.vigo.ist/6";
const sentryStagingDsn = "https://4b66af2a7f29436fa65ca3e0e977dc43@sentry.vigo.ist/5";
const sentryProdDsn = "https://a219c59b916bb088979b6e027b0a3990@apm.vigo.ist/7";
import "./i18n";

const selectSentryDsn = () => {
    switch (process.env.REACT_APP_ENV) {
        case "development":
            return sentryDevDsn;
        case "staging":
            return sentryStagingDsn;
        case "production":
            return sentryProdDsn;
        default:
            return sentryDevDsn;
    }
};

const isLocal = process.env.REACT_APP_IS_LOCAL;
const isDev = process.env.NODE_ENV === "development";

if (!isLocal) {
    Sentry.init({
        dsn: selectSentryDsn(),
        release: "1.8.6",
        dist: "1.8.6",
        environment: process.env.REACT_APP_ENV || "development",
        enableReplay: true,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: false,
                networkDetailAllowUrls: [window.location.origin]
            })
        ],
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control
        tracesSampleRate: 1.0,
        ignoreErrors: ["ResizeObserver loop limit exceeded"],
        debug: isDev ? true : false,
        devtool: "hidden-source-map",
        beforeSend(event) {
            if (isDev && event.exception && event.exception.values) {
                event.exception.values.forEach(exception => {
                    if (exception.stacktrace && exception.stacktrace.frames) {
                        exception.stacktrace.frames.forEach(frame => {
                            if (
                                frame.filename &&
                                frame.filename.startsWith(window.location.origin + "/")
                            ) {
                                frame.filename = frame.filename.replace(
                                    window.location.origin + "/",
                                    "~/"
                                );
                            }
                        });
                    }
                });
            }
            return event;
        }
    });
}

export const setSentryUser = user => {
    if (user) {
        Sentry.setUser({
            id: user.userId,
            username: `${user.username}`
        });
        const subcompany =
            user?.subcompanies && user.subcompanies.length > 0 ? user.subcompanies[0] : null;
        const company = user?.companies && user.companies.length > 0 ? user.companies[0] : null;
        Sentry.setTag("user.fullName", user.fullName);
        Sentry.setTag("subcompany.name", subcompany?.name);
        Sentry.setTag("subcompany.id", subcompany?._id);
        Sentry.setTag("subcompany.city", subcompany?.city);
        Sentry.setTag("company.name", company?.companyName);
        Sentry.setTag("company.id", company?._id);
        Sentry.setTag(
            "company.type",
            user?.pool?.integrationType === "dedicatedIntegration"
                ? "Havuz"
                : subcompany?.subCompanyWorkType === "dedicated"
                ? "Dedike"
                : subcompany?.subCompanyWorkType === "pool"
                ? "Havuz"
                : subcompany?.subCompanyWorkType
        );
    } else {
        Sentry.setUser(null);
        Sentry.setTag("user.fullName", null);
        Sentry.setTag("subcompany.name", null);
        Sentry.setTag("subcompany.city", null);
        Sentry.setTag("subcompany.id", null);
        Sentry.setTag("company.name", null);
        Sentry.setTag("company.id", null);
        Sentry.setTag("company.type", null);
    }
};

// Define new properties / abilities for objects in this area
// Capitalize first letter: "String".capitalize()
// Use with optional chaining operator (?.) for unexpected non-string null values
Object.defineProperty(String.prototype, "capitalize", {
    value: function(locale = "tr-TR") {
        if (this && this.length > 1) {
            return this.charAt(0).toLocaleUpperCase(locale) + this.slice(1);
        }
        return this;
    },
    enumerable: false
});

ReactDOM.render(<App />, document.getElementById("root"));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
